import React, { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge, LoadError, Loading, Welcome, fmt, propName, timeLabel, useLoad, usePortal } from '../../components/clientportal/kit';
import { NewCleanForm, VisitDetail, isPast, visitState } from '../../components/clientportal/VisitDrawers';
import { CLIENT_REQUEST_KINDS, RequestList, isOpenRequest } from '../../components/clientportal/requests';

const TABS = ['Upcoming', 'Past', 'Requests'];

const VisitRow = ({ v, property, onOpen }) => (
  <article className="visit" data-testid={`visit-${v.id}`}>
    <div className="visit-date"><small>{fmt(v.start_time, { month: 'short' }).toUpperCase()}</small>{fmt(v.start_time, { day: '2-digit' })}</div>
    <div><h2>{v.service_name || 'Your clean'}</h2><p>{propName(property)} · {timeLabel(v.start_time)} PT</p><p>Your Guru: {v.cleaner_name || 'Being assigned'}</p></div>
    <div className="visit-actions"><Badge>{visitState(v)}</Badge><button className="text-button" onClick={onOpen} data-testid={`visit-open-${v.id}`}>View clean ↗</button></div>
  </article>
);

export default function ClientCleansPage() {
  const { id: routeId } = useParams();
  const [params, setParams] = useSearchParams();
  const { openDrawer } = usePortal();
  const [visits, reloadVisits, vErr] = useLoad('/api/portal/appointments');
  const [props] = useLoad('/api/clients/me/addresses');
  const [mine, reloadMine] = useLoad('/api/review-items/mine');
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'Upcoming';
  const requests = useMemo(() => (mine || []).filter((r) => CLIENT_REQUEST_KINDS.includes(r.kind) && r.kind !== 'quote_decision'), [mine]);
  const pending = requests.filter(isOpenRequest).length;
  const propFor = (v) => (props || []).find((p) => p.id === v.address_id);
  const changed = () => { reloadVisits(); reloadMine(); };
  const open = (v) => openDrawer({ title: 'Your clean', content: <VisitDetail visit={v} property={propFor(v)} onChanged={changed} /> });
  const target = routeId || params.get('visit');
  useEffect(() => {
    const v = target && (visits || []).find((x) => x.id === target);
    if (v && props) open(v);
  }, [target, visits, props]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (visits || []).filter((v) => (tab === 'Upcoming' ? !isPast(v) : isPast(v)))
    .sort((a, b) => (tab === 'Upcoming' ? a.start_time.localeCompare(b.start_time) : b.start_time.localeCompare(a.start_time)));
  const setTab = (t) => { const n = new URLSearchParams(params); n.set('tab', t); n.delete('visit'); setParams(n, { replace: true }); };
  return (
    <div data-testid="client-cleans">
      <Welcome title="My Cleans" desc="See what’s ahead and revisit your completed cleans."
        action={<button className="primary" data-testid="cleans-request-clean" onClick={() => openDrawer({ title: 'Request another clean', content: <NewCleanForm properties={props || []} onDone={changed} /> })}>Request a clean ＋</button>} />
      <div className="tabs" role="group" aria-label="Clean views">
        {TABS.map((t) => <button key={t} className={t === tab ? 'active' : ''} aria-pressed={t === tab} onClick={() => setTab(t)} data-testid={`cleans-tab-${t.toLowerCase()}`}>{t}{t === 'Requests' && pending ? ` (${pending})` : ''}</button>)}
      </div>
      {vErr && !visits ? <LoadError text={vErr} onRetry={reloadVisits} /> : !visits ? <Loading /> : tab === 'Requests'
        ? <RequestList requests={requests} properties={props} onChanged={changed} />
        : rows.length ? rows.map((v) => <VisitRow key={v.id} v={v} property={propFor(v)} onOpen={() => open(v)} />)
          : <div className="panel empty" data-testid="cleans-empty">{tab === 'Upcoming' ? 'No upcoming cleans. Request a clean and your team will confirm the details.' : 'Your completed cleans will appear here.'}</div>}
      <p className="muted">Change requests are reviewed by your team. Your confirmed appointment stays in place until a change is confirmed.</p>
    </div>
  );
}
